import React, { useState, useRef } from 'react';
import { ArrowRight, CheckCircle, Star, Wrench, Settings, Hammer, Home as HomeIcon, Building, Lightbulb, Trophy, Award, Leaf, ChevronLeft, ChevronRight, User, Linkedin, Phone, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { services, clients, projects, awards, stats, team } from '../mock';
import { toast } from "sonner";
import ShopBar from '../components/ShopBar';
import CredentialRibbon from "../components/CredentialRibbon";
import ConsultBand from '../components/ConsultBand';


const iconMap = {
  wrench: Wrench,
  settings: Settings,
  tool: Hammer,
  home: HomeIcon,
  building: Building,
  lightbulb: Lightbulb
};

const awardIconMap = {
  trophy: Trophy,
  award: Award,
  star: Star,
  leaf: Leaf
};

export const Home = () => {
  
  const projectsScrollRef = useRef(null);
  const awardsScrollRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

const handleSubmit = async (e) => {
  e.preventDefault();

  console.log("FORM SUBMIT TRIGGERED", formData);

  // Track Google Ads conversion
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion_event_contact', {
        'send_to': 'AW-17963167817',
      });
    }

  const formUrl = "https://docs.google.com/forms/d/e/1FAIpQLScYHiUKJ4xt9EZhF_CLzHGnrTxV1UFQEh6a78r0WYzl2i5pGg/formResponse";


  const formDataToSend = new FormData();
  formDataToSend.append("entry.663475068", formData.name);
  formDataToSend.append("entry.1351859543", formData.phone);
  formDataToSend.append("entry.266053166", formData.email);
  formDataToSend.append("entry.377423970", formData.message);

  try {
    await fetch(formUrl, {
      method: "POST",
      body: formDataToSend,
      mode: "no-cors",
    });

    toast.success("Thank you for contacting us!", {
      description: "We will get back to you in 1-2 business days.",
    });


    setFormData({
      name: "",
      phone: "",
      email: "",
      message: "",
    });
  } catch (error) {
    toast.error("Something went wrong", {
  description: "Please email us at admin@citicool.in.",
});
  }
};

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const scrollProjects = (direction) => {
    if (projectsScrollRef.current) {
      const scrollAmount = 400;
      projectsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollAwards = (direction) => {
    if (awardsScrollRef.current) {
      const scrollAmount = 350;
      awardsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="pt-16 lg:pt-20">
      <ShopBar />
      {/* Hero Section */}
      <section id="home" className="relative flex items-center min-h-[auto] lg:min-h-screen">
        {/* Background Image - Optimized for faster loading */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/home-hero.webp"
            srcSet="/home-hero-800.webp 800w, /home-hero-1280.webp 1280w, /home-hero.webp 1920w"
            sizes="100vw"
            alt="Professional HVAC technician installing Daikin VRV AC system in Delhi NCR - Citi Cool authorized dealer" 
            className="w-full h-full object-cover object-[65%_center] lg:object-center"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70 lg:bg-gradient-to-r lg:from-black/70 lg:via-black/50 lg:to-black/30"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-20">
            <div className="max-w-3xl">
              <div className="flex flex-wrap gap-2 mb-3 lg:mb-8">
                <span className="bg-white/90 backdrop-blur-sm text-blue-700 px-3 py-1 lg:px-4 lg:py-2 rounded-full text-xs lg:text-sm font-medium shadow-sm border border-blue-200">
                  Authorized Daikin Dealer
                </span>
                <span className="bg-white/90 backdrop-blur-sm text-blue-700 px-3 py-1 lg:px-4 lg:py-2 rounded-full text-xs lg:text-sm font-medium shadow-sm border border-blue-200">
                  Authorized O-General Dealer
                </span>
                <span className="bg-white/90 backdrop-blur-sm text-blue-700 px-3 py-1 lg:px-4 lg:py-2 rounded-full text-xs lg:text-sm font-medium shadow-sm border border-blue-200">
                  Authorized Mitsubishi Heavy Dealer
                </span>
                <span className="bg-white/90 backdrop-blur-sm text-blue-700 px-3 py-1 lg:px-4 lg:py-2 rounded-full text-xs lg:text-sm font-medium shadow-sm border border-blue-200">
                  Authorized Hitachi Dealer
                </span>
              </div>
              
              <h1 className="text-2xl sm:text-3xl lg:text-6xl font-bold text-white leading-tight mb-2 lg:mb-6">
                Engineering Comfort, Delivering Excellence since 1990
              </h1>
              
              <p className="text-sm lg:text-2xl text-white/95 font-medium mb-2 lg:mb-4">
                Experience ultimate comfort with Daikin, O-General, Hitachi and Mitsubishi Heavy Industries' world-class air conditioning systems.
              </p>
        
              
              <div className="flex flex-row gap-2 lg:gap-4 mt-4 lg:mt-0">
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white transition-all duration-300 hover:shadow-lg text-xs lg:text-sm h-9 px-3 lg:h-11 lg:px-6"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get in Touch
                  <ArrowRight className="ml-1 lg:ml-2" size={16} />
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300 hover:shadow-lg text-xs lg:text-sm h-9 px-3 lg:h-11 lg:px-6"
                  onClick={() => {
                    if (window.gtagSendEvent) {
                      window.gtagSendEvent('tel:+917703900307');
                    } else {
                      window.location.href = 'tel:+917703900307';
                    }
                  }}
                >
                  <Phone className="mr-1 lg:mr-2" size={16} />
                  Call Now
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 transition-all duration-300 text-xs lg:text-sm h-9 px-3 lg:h-11 lg:px-6"
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                >
                  Our Services
                </Button>
              </div>

              {/* Brand Logos - Scrolling Marquee */}
              <div className="mt-4 lg:mt-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl py-3 lg:py-4 border border-white/40 overflow-hidden">
                  <div className="flex animate-marquee whitespace-nowrap">
                    {[...Array(2)].map((_, setIndex) => (
                      <div key={setIndex} className="flex items-center gap-8 sm:gap-12 lg:gap-16 shrink-0 px-4 sm:px-6 lg:px-8">
                        <img src="/O General.webp" alt="Authorized O-General Dealer Delhi NCR" className="h-4 sm:h-5 lg:h-7 w-auto object-contain" />
                        <img src="/daikin.webp" alt="Authorized Daikin Dealer Delhi NCR" className="h-6 sm:h-7 lg:h-10 w-auto object-contain" />
                        <img src="/Mitsubishi Heavy Industries.webp" alt="Mitsubishi Heavy Industries AC Delhi NCR" className="h-6 sm:h-7 lg:h-10 w-auto object-contain" />
                        <img src="/hitachi-logo.webp" alt="Authorized Hitachi AC Dealer Delhi NCR" className="h-20 sm:h-22 lg:h-24 w-auto object-contain" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-blue-700 to-indigo-700 py-5 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-8">
            {stats.map((stat, index) => (
              <div key={stat.id} className={`text-center ${index === 3 ? 'hidden lg:block' : ''}`}>
                <div className="text-2xl lg:text-5xl font-bold text-white mb-0.5 lg:mb-1">{stat.value}</div>
                <div className="text-blue-100 font-medium text-xs lg:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Authorized Dealership Certificates Section */}
      <section className="pt-10 pb-4 lg:pt-14 lg:pb-6 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-2">
              <ShieldCheck className="text-blue-600" size={22} />
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Verified & Authorized</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-1">Dealership Authorization Certificates</h2>
            <p className="text-gray-600 mt-2 max-w-2xl mx-auto text-base lg:text-lg">
              Officially authorized by Daikin, O-General, Hitachi and Mitsubishi Heavy Industries to deliver genuine products and certified service. Tap a certificate to read it in full.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div data-testid="certificate-daikin" className="bg-gray-50 rounded-2xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <img
                src="/Daikin Authorized Dealership Certificate.webp"
                data-zoomable="true"
                tabIndex={0}
                alt="Daikin Partners in Excellence - Citicool Corporation Authorized Dealership Certificate 2025-2026"
                className="w-full h-auto rounded-lg"
                loading="lazy"
              />
            </div>
            <div data-testid="certificate-ogeneral" className="bg-gray-50 rounded-2xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <img
                src="/O-General Authorized Dealership Certificate.webp"
                data-zoomable="true"
                tabIndex={0}
                alt="O-General Authorized Sales and Service Dealer Certificate - Citicool Corporation"
                className="w-full h-auto rounded-lg"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* RECOGNITION (Awards) Section */}
      <section className="pt-8 pb-10 lg:pt-10 lg:pb-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Recognition</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2">Awards & Certifications</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
              Our commitment to excellence has been recognized by industry leaders
            </p>
          </div>
          <div className="relative">
            <div 
              ref={awardsScrollRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {awards.map((award) => {
                const IconComponent = awardIconMap[award.icon];
                return (
                  <Card key={award.id} className="flex-shrink-0 w-72 border-none shadow-lg text-center hover:shadow-xl transition-all duration-300 bg-white">
                    <CardContent className="pt-8 pb-6">
                      {/* Award Image - Fixed consistent size */}
                      <div className="w-full aspect-square mb-4 rounded-lg overflow-hidden bg-white flex items-center justify-center p-4 border border-gray-100">
                        {award.image ? (
                          <img src={award.image} alt={award.title} loading="lazy" className="max-w-full max-h-full object-contain" />
                        ) : (
                          <Trophy className="text-amber-500" size={72} aria-hidden="true" />
                        )}
                      </div>
                      {award.year && (<div className="text-2xl font-bold text-blue-600 mb-2">{award.year}</div>)}
                      <h3 className="font-bold text-gray-900 mb-2">{award.title}</h3>
                      <p className="text-sm text-gray-600">{award.organization}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <div className="flex justify-center gap-4 mt-6">
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollAwards('left')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <ChevronLeft size={20} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollAwards('right')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <ChevronRight size={20} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION - Includes Our Approach + Services */}
      <section className="pt-8 pb-12 lg:pt-12 lg:pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Our Approach */}
          <div className="mb-20">
            {/* Centered Heading */}
            <div className="text-center mb-12">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Our Approach</span>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2 leading-tight">Engineering Comfort with Precision</h2>
            </div>
            
            {/* Two Column Layout: Image Left, Text Right on Desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Image - Left on Desktop, After heading on Mobile */}
              <div className="order-2 lg:order-1">
                <img loading="lazy" decoding="async" 
                  src="/Daikin VRV Engineers.webp" 
                  alt="Daikin and O-General authorized service engineers - Professional HVAC maintenance Delhi NCR" 
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
              
              {/* Text Content - Right on Desktop, After image on Mobile */}
              <div className="space-y-6 order-3 lg:order-2">
                <p className="text-gray-600 leading-relaxed text-lg">
                  As an authorized Daikin, O-General, Hitachi and Mitsubishi Heavy Industries dealer, we bring decades of expertise in climate control solutions. Our commitment extends beyond installation to creating environments that enhance well-being.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Every project is approached with meticulous attention to detail, ensuring optimal performance and energy efficiency while maintaining the aesthetic integrity of your space.
                </p>
                <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">What Sets Us Apart</h3>
                  <ul className="space-y-3">
                    {[
                      'Custom design and engineering for every project',
                      'End-to-end implementation with professional project management',
                      'HVAC and energy consultation by experts',
                      'Professional project delivery with meticulous attention to detail',
                      'Over 35 years of industry excellence since 1990'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Our Services */}
          <div id="services" className="text-center mb-16 scroll-mt-24">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Our Services</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2">Comprehensive HVAC Solutions</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
              From design to implementation, we provide complete climate control solutions with professional project management.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white">
                  <CardHeader>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-blue-600" size={24} />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <ConsultBand from="home" className="mt-12" />
        </div>
      </section>

      {/* PROJECTS Section */}
      <section id="projects" className="py-20 lg:py-32 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Our Work</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2">Spaces We Air-Condition</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
              Work delivered across Delhi NCR, shown by type of space.
            </p>
            <p className="text-gray-600 mt-2 text-sm">Images are illustrative.</p>
          </div>
          <div className="relative">
            <div 
              ref={projectsScrollRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {projects.map((project) => (
                <Card key={project.id} className="flex-shrink-0 w-80 border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-0">
                    {project.image && (
                      <img loading="lazy" decoding="async"
                        src={project.image}
                        alt={project.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                      />
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                      <p className="text-blue-600 text-sm font-medium mb-2">{project.location}</p>
                      <p className="text-gray-600 text-sm">{project.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex justify-center gap-4 mt-6">
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollProjects('left')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <ChevronLeft size={20} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollProjects('right')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <ChevronRight size={20} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT Section - Company + Team + Clients */}
      <section id="about" className="py-20 lg:py-32 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* About Citi Cool */}
          <div className="max-w-4xl mx-auto text-center mb-20">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">About Us</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2 mb-8">Citi Cool Corporation</h2>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Established in 1990, Citi Cool Corporation has been at the forefront of delivering premium HVAC solutions across India for over three decades. As authorized dealers for both Daikin, O-General, Hitachi and Mitsubishi Heavy Industries we bring world-class air conditioning systems to residential and commercial spaces throughout the country.
              </p>
              <p>
                Our journey began with a simple mission: to engineer comfort with precision. Today, we stand as a trusted name in the HVAC industry, having successfully completed over 1500 projects ranging from luxury residential complexes to large-scale commercial installations.
              </p>
              <p>
                What sets us apart is our comprehensive approach to climate control. We don't just install air conditioning systems—we design custom solutions tailored to your space, manage end-to-end implementation with professional project management, and provide ongoing HVAC and energy consultation. Our team combines deep technical expertise with a commitment to excellence that has earned us industry recognition and the trust of leading organizations across India.
              </p>
            </div>
          </div>

          {/* Team */}
          <div className="mb-20">
            <div className="text-center mb-16">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Our Team</span>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2">Leadership & Expertise</h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
                Industry veterans with decades of experience in HVAC engineering and professional project management
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {team.map((member) => (
                <Card key={member.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white relative">
                  <CardContent className="pt-8 pb-6">
                    <div className="text-center">
                      {/* Founder Image with consistent framing */}
                      <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-blue-100 shadow-lg">
                        <img loading="lazy" decoding="async" 
                          src={member.image} 
                          alt={member.name}
                          className={`w-full h-full ${member.id === 1 ? 'object-cover object-top' : 'object-cover'}`}
                        />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                      <p className="text-blue-600 font-semibold mb-4">{member.role}</p>
                      <p className="text-gray-600 leading-relaxed mt-4 mb-12">{member.description}</p>
                    </div>
                    {/* LinkedIn link positioned at bottom right */}
                    {member.linkedin && (
                      <div className="absolute bottom-4 right-4">
                        <a 
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
                          title="Connect on LinkedIn"
                        >
                          <Linkedin size={20} />
                          <span className="text-sm font-medium">LinkedIn</span>
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Clients */}
          <div id="clients">
            <div className="text-center mb-16">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Our Clients</span>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2">Trusted by Leading Organizations</h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
                We're proud to serve some of India's most prestigious companies and institutions
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {clients.map((client) => (
                <Card key={client.id} className="border-none shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center p-6">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-3 bg-white rounded-lg flex items-center justify-center">
                      <Building className="text-blue-600" size={32} />
                    </div>
                    <p className="text-sm font-medium text-gray-900">{client.name}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GET IN TOUCH (Contact) Section */}
      <section id="contact" className="py-20 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">Get in Touch</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2">Start Your Project</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
              Share your requirements and we'll provide a tailored consultation
            </p>
          </div>
          
          {/* Contact Form */}
          <Card className="border-none shadow-xl bg-white">
            <CardHeader>
              <CardTitle className="text-2xl">Send Us a Message</CardTitle>
              <CardDescription className="text-base">Fill out the form and we'll get back to you within 24 hours.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name" 
                    required 
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 77039 00307" 
                    required 
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    id="email" 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com" 
                    required 
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea 
                    id="message" 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements..." 
                    required 
                    className="mt-2 min-h-32"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-all duration-300"
                  size="lg"
                >
                  Send Message
                </Button>
                <p className="text-xs text-gray-500 text-center mt-3">
  By submitting this form, you agree to our{" "}
  <a
    href="/privacy-policy"
    className="text-blue-600 hover:underline"
  >
    Privacy Policy
  </a>.
</p>

              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
